<template>
  <div class="revisions-panel">

    <p v-if="loading" class="loading-state">Loading revisions…</p>

    <div v-else-if="error" class="content-error">
      <p class="content-error-text">{{ error }}</p>
      <button type="button" class="btn btn-secondary" @click="refresh">Retry</button>
    </div>

    <template v-else>
      <!-- ─── Status strip ─────────────────────────────────────────── -->
      <div class="status-strip">
        <div class="status-item">
          <span class="status-label">Revisions</span>
          <span class="status-value">{{ revisions.length }}</span>
        </div>
        <div class="status-item">
          <span class="status-label">Current</span>
          <span class="status-value">
            {{ impact && impact.head_revision ? `Revision ${impact.head_revision}` : 'None yet' }}
          </span>
        </div>
        <div class="status-item">
          <span class="status-label">Items</span>
          <span class="status-value">{{ impact ? impact.item_count : '–' }}</span>
        </div>
        <div class="status-item">
          <span class="status-label">Submissions</span>
          <span class="status-value">{{ impact ? impact.total_submissions : '–' }}</span>
        </div>
      </div>

      <!-- ─── State explanation ────────────────────────────────────── -->
      <p v-if="!hasRevisions" class="state-note">
        This questionnaire has never been frozen. While it has no revisions it is in pure
        authoring: edit freely, nothing is recorded, no warnings. Freezing it captures every
        item, its order and all four locales, so data collected against it stays readable
        even after the library questions are later edited.
      </p>
      <p v-else-if="!differs" class="state-note ok">
        The questionnaire matches revision {{ impact.head_revision }} exactly. There is
        nothing to freeze.
      </p>
      <p v-else class="state-note warn">
        The questionnaire has changed since revision {{ impact.head_revision }}. Freezing
        creates revision {{ impact.head_revision + 1 }};
        <template v-if="impact.total_submissions > 0">
          the {{ impact.total_submissions }}
          {{ impact.total_submissions === 1 ? 'response' : 'responses' }} already collected
          stay pinned to earlier revisions and remain readable exactly as answered.
        </template>
        <template v-else>no data has been collected yet.</template>
      </p>

      <!-- ─── Affected surveys ─────────────────────────────────────── -->
      <div v-if="impact && impact.surveys.length > 0" class="surveys">
        <p class="surveys-title">Surveys using this questionnaire</p>
        <ul class="survey-list">
          <li v-for="s in impact.surveys" :key="s.id" class="survey-row">
            <span class="survey-name">{{ s.name }}</span>
            <code class="mono-inline">{{ s.slug }}</code>
            <span class="survey-status" :class="`status-${s.status}`">{{ s.status }}</span>
            <span class="survey-count">
              {{ s.submission_count }}
              {{ s.submission_count === 1 ? 'submission' : 'submissions' }}
            </span>
          </li>
        </ul>
      </div>

      <!-- ─── Freeze action ────────────────────────────────────────── -->
      <div class="freeze-box">
        <template v-if="hasRevisions">
          <div class="field-row">
            <label class="field">
              <span class="field-label">Change kind</span>
              <select v-model="changeKind" class="field-input" :disabled="!differs || minting">
                <option value="cosmetic">Cosmetic: spelling, punctuation, formatting</option>
                <option value="substantive">Substantive: meaning, options, or order changed</option>
              </select>
            </label>
          </div>
          <label class="field">
            <span class="field-label">
              What changed
              <span class="field-hint">
                Read later by whoever analyses this data. "Fixed spelling of Gemüse in Q4"
                beats "update".
              </span>
            </span>
            <input
              v-model="changeNote"
              type="text"
              class="field-input"
              maxlength="2000"
              placeholder="Describe the change"
              :disabled="!differs || minting"
            />
          </label>
        </template>

        <div class="freeze-actions">
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canMint"
            @click="onMint"
          >{{ mintLabel }}</button>
          <span v-if="hasRevisions && differs && !noteValid" class="freeze-hint">
            A note is required.
          </span>
        </div>
      </div>

      <!-- ─── History ──────────────────────────────────────────────── -->
      <div v-if="revisions.length > 0" class="history">
        <p class="history-title">History</p>
        <ul class="revision-list">
          <li v-for="r in revisions" :key="r.id" class="revision-row">
            <div class="revision-head">
              <span class="revision-number">Revision {{ r.revision }}</span>
              <span class="revision-kind" :class="`kind-${r.change_kind}`">{{ r.change_kind }}</span>
              <span class="revision-date">{{ formatDate(r.created_at) }}</span>
            </div>
            <p v-if="r.change_note" class="revision-note">{{ r.change_note }}</p>
            <div class="revision-foot">
              <code class="mono-inline hash">{{ r.payload_hash.slice(0, 12) }}</code>
              <button
                type="button"
                class="link-button"
                @click="onInspect(r.revision)"
              >{{ inspecting === r.revision ? 'Hide' : 'Inspect' }}</button>
            </div>
            <pre v-if="inspecting === r.revision" class="revision-dump">{{ inspectDump }}</pre>
          </li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script>
// QuestionnaireRevisionsPanel
//
// Freezes a questionnaire into an immutable revision, and shows the
// history of those freezes. See docs/CONTRACT_DATA_CAPTURE.md.
//
// The panel deliberately reports three distinct states rather than just
// offering a button, because they mean different things to an author:
// never frozen (pure authoring, edit freely), matching head (nothing to
// do), and diverged (freezing will create a revision, and here is what
// that affects). Minting when nothing changed is refused by the backend
// as a no-op, so the button is disabled rather than letting a user
// discover that by pressing it.

import apiService from '@/services/apiService';

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export default {
  name: 'QuestionnaireRevisionsPanel',
  props: {
    questionnaireSlug: { type: String, required: true },
  },
  emits: ['show-error', 'show-success'],
  data() {
    return {
      loading: false,
      error: null,
      revisions: [],
      impact: null,
      changeKind: 'cosmetic',
      changeNote: '',
      minting: false,
      inspecting: null,
      inspectDump: '',
    };
  },
  computed: {
    hasRevisions() {
      return !!(this.impact && this.impact.has_revisions);
    },
    /**
     * Whether the live questionnaire diverges from the head revision.
     * When there are no revisions at all the backend reports true, which
     * is what we want: the first freeze is always available.
     */
    differs() {
      return !!(this.impact && this.impact.differs_from_head);
    },
    noteValid() {
      return this.changeNote.trim().length > 0;
    },
    canMint() {
      if (this.minting || !this.differs) return false;
      // The initial revision is 'initial' and carries no note; every
      // later one must say what changed.
      return this.hasRevisions ? this.noteValid : true;
    },
    mintLabel() {
      if (this.minting) return 'Freezing…';
      if (!this.hasRevisions) return 'Freeze as revision 1';
      if (!this.differs) return 'Nothing to freeze';
      return `Freeze as revision ${this.impact.head_revision + 1}`;
    },
  },
  watch: {
    questionnaireSlug: {
      immediate: true,
      handler() { this.refresh(); },
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return '';
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? '' : dateFmt.format(d);
    },

    async refresh() {
      this.loading = true;
      this.error = null;
      this.inspecting = null;
      try {
        const [revisions, impact] = await Promise.all([
          apiService.fetchQuestionnaireRevisions(this.questionnaireSlug),
          apiService.fetchQuestionnaireEditImpact(this.questionnaireSlug),
        ]);
        this.revisions = revisions;
        this.impact = impact;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.error = typeof detail === 'string'
          ? detail
          : 'Could not load revisions.';
      } finally {
        this.loading = false;
      }
    },

    async onMint() {
      if (!this.canMint) return;
      this.minting = true;
      try {
        const body = this.hasRevisions
          ? { change_kind: this.changeKind, change_note: this.changeNote.trim() }
          : {};
        const result = await apiService.mintQuestionnaireRevision(
          this.questionnaireSlug, body,
        );
        if (result.created) {
          this.changeNote = '';
          this.$emit('show-success', `Frozen as revision ${result.revision.revision}.`);
        } else {
          // Backend refused a no-op. Surface it plainly, since the author
          // believed something had changed, and it hadn't.
          this.$emit('show-error', 'Nothing changed, so no revision was created.');
        }
        await this.refresh();
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.$emit('show-error', typeof detail === 'string'
          ? detail
          : 'Could not freeze this questionnaire.');
      } finally {
        this.minting = false;
      }
    },

    async onInspect(revision) {
      if (this.inspecting === revision) {
        this.inspecting = null;
        this.inspectDump = '';
        return;
      }
      this.inspecting = revision;
      this.inspectDump = 'Loading…';
      try {
        const full = await apiService.fetchQuestionnaireRevision(
          this.questionnaireSlug, revision,
        );
        this.inspectDump = JSON.stringify(full.payload, null, 2);
      } catch (e) {
        this.inspectDump = 'Could not load this revision.';
      }
    },
  },
};
</script>

<style scoped>
.revisions-panel {
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-accent: #534AB7;
  --color-warn-bg: #FDF6E3;
  --color-warn-border: #E0C980;
  --color-ok-bg: #F1F7F1;
  --color-ok-border: #B9D4B9;
  --radius-md: 8px;
  font-size: 14px;
  color: var(--color-text-primary);
}

.loading-state,
.content-error-text {
  color: var(--color-text-tertiary);
  font-size: 13px;
}

/* ── Status strip ── */
.status-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.02);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  margin-bottom: 14px;
}

.status-item { display: flex; flex-direction: column; gap: 2px; }

.status-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
}

.status-value { font-size: 15px; font-weight: 500; }

/* ── State note ── */
.state-note {
  margin: 0 0 14px;
  padding: 10px 14px;
  border-radius: var(--radius-md);
  border: 0.5px solid var(--color-border);
  background: rgba(0, 0, 0, 0.02);
  color: var(--color-text-secondary);
  line-height: 1.5;
  font-size: 13px;
}

.state-note.ok { background: var(--color-ok-bg); border-color: var(--color-ok-border); }
.state-note.warn { background: var(--color-warn-bg); border-color: var(--color-warn-border); }

/* ── Surveys ── */
.surveys { margin-bottom: 14px; }

.surveys-title,
.history-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  margin: 0 0 6px;
}

.survey-list, .revision-list { list-style: none; margin: 0; padding: 0; }

.survey-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 7px 0;
  border-bottom: 0.5px solid var(--color-border);
}

.survey-name { font-weight: 500; }
.survey-status { font-size: 11px; text-transform: uppercase; color: var(--color-text-tertiary); }
.survey-count { font-size: 12px; color: var(--color-text-secondary); margin-left: auto; }

.mono-inline {
  font-family: "SF Mono", Menlo, Consolas, monospace;
  font-size: 12px;
  background: rgba(0, 0, 0, 0.05);
  padding: 1px 5px;
  border-radius: 4px;
}

/* ── Freeze box ── */
.freeze-box {
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  margin-bottom: 18px;
}

.field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; }
.field-label { font-size: 12px; font-weight: 500; }

.field-hint {
  display: block;
  font-weight: 400;
  color: var(--color-text-tertiary);
  font-size: 11px;
  line-height: 1.45;
  margin-top: 2px;
}

.field-input {
  font-family: inherit;
  font-size: 13px;
  padding: 7px 10px;
  border: 0.5px solid var(--color-border);
  border-radius: 6px;
  background: white;
  width: 100%;
}

.field-input:disabled { opacity: 0.5; cursor: not-allowed; }

.freeze-actions { display: flex; align-items: center; gap: 12px; }

.freeze-hint { font-size: 12px; color: var(--color-text-tertiary); }

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  background: white;
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn:disabled { opacity: 0.45; cursor: not-allowed; }

/* ── History ── */
.revision-row { padding: 10px 0; border-bottom: 0.5px solid var(--color-border); }

.revision-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.revision-number { font-weight: 500; }

.revision-kind {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 1px 7px;
  border-radius: 10px;
  border: 0.5px solid var(--color-border);
  color: var(--color-text-secondary);
}

.kind-substantive { background: var(--color-warn-bg); border-color: var(--color-warn-border); }
.kind-initial { background: rgba(83, 74, 183, 0.10); border-color: rgba(83, 74, 183, 0.35); }

.revision-date { font-size: 12px; color: var(--color-text-tertiary); margin-left: auto; }

.revision-note {
  margin: 5px 0 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.45;
}

.revision-foot { display: flex; align-items: center; gap: 12px; margin-top: 6px; }
.hash { color: var(--color-text-tertiary); }

.link-button {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: var(--color-accent);
  cursor: pointer;
  text-decoration: underline;
}

.revision-dump {
  max-height: 320px;
  overflow: auto;
  background: rgba(0, 0, 0, 0.04);
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  font-size: 11px;
  font-family: "SF Mono", Menlo, Consolas, monospace;
  line-height: 1.45;
  margin: 8px 0 0;
  white-space: pre;
}
</style>
